import React, { useEffect, useRef } from 'react';

import { X } from 'lucide-react';

import { CourseMinifiedDTO } from '../../services/apiClient';
import { formatDistance } from '../../utils/geoUtils';
import ClosedBadge from '../common/ClosedBadge';
import CourseCompactCard from '../maps/CourseCompactCard';
import CourseContactLinks from '../maps/CourseContactLinks';
import CourseHero from '../maps/CourseHero';
import CourseStateToggles from '../maps/CourseStateToggles';
import ShareCourseButton from '../maps/ShareCourseButton';

interface CourseDetailDrawerProps {
  // Nullable rather than "render me only when open": the drawer stays mounted so it can animate
  // out as well as in. See `content` below.
  course: CourseMinifiedDTO | null;
  distanceMeters?: number;
  // The next course in the shared "nearby" ordering — the same sequence the strip scrolls and
  // the list view pages. Not the geographically closest club to `course`; see Explore.tsx.
  nextCourse?: CourseMinifiedDTO | null;
  isPlayed?: boolean;
  isBucketListed?: boolean;
  onClose: () => void;
  // Takes a course rather than being fixed to the open one: the next-nearby row at the bottom
  // fires it for *its* course, not the one in the header.
  onSelectCourse: (course: CourseMinifiedDTO) => void;
  onBucketList: (course: CourseMinifiedDTO) => void;
  onTogglePlayed: (course: CourseMinifiedDTO) => void;
}

// Everything the panel paints, captured together. Frozen as one unit while the drawer slides out —
// see the ref below. Freezing the course alone is not enough: the next course is derived from the
// selected one, so it would blank a frame after the course was cleared and the section would
// collapse mid-animation.
interface DrawerContent {
  course: CourseMinifiedDTO;
  distanceMeters?: number;
  nextCourse?: CourseMinifiedDTO | null;
}

// A section band. The reference design separates blocks with a flat gap rather than a rule, which
// is what stops the panel reading as one long column of text.
const SectionBreak: React.FC = () => (
  <div className="h-2 shrink-0 bg-black/25" />
);

// The map's detail surface for the pin that was just clicked — a panel docked to the right edge,
// under the top bar. Replaces the old floating CourseDetailPanel card, which sat over the
// top-left of the map.
//
// Layout follows the Thousand Greens "Club Details" panel: a titled header bar, a club identity
// block, then stacked sections, with the calls-to-action pinned to the bottom. Structure only —
// the colours stay on this app's dark charcoal/primary brand palette, not the reference's light
// one.
//
// Deliberately NOT built on components/common/Drawer. That one is a modal: it portals to <body>,
// paints a full-screen backdrop that closes on click, and locks page scroll. On a map that is
// wrong — the backdrop would swallow every click, so a visitor could not pan the map or pick the
// next pin without closing the drawer first. This one is non-modal and scoped to the Explore
// page, so the map stays live behind it.
//
// A 24rem column from `sm` up. On a phone it is full width and covers the map, so there it only
// opens on a deliberate View (a strip card or its View pill), never on a pin tap: see
// handlePinOnStrip in Explore.tsx.
const CourseDetailDrawer: React.FC<CourseDetailDrawerProps> = ({
  course,
  distanceMeters,
  nextCourse,
  isPlayed = false,
  isBucketListed = false,
  onClose,
  onSelectCourse,
  onBucketList,
  onTogglePlayed,
}) => {
  // Keep painting the last course while the panel slides out. Without this the content blanks the
  // instant the course is cleared and the drawer animates away empty.
  //
  // The ref is written in an effect, not in the render body: React may render and then discard a
  // pass (StrictMode's double render, a concurrent interruption), and a ref mutated during render
  // makes that thrown-away work visible to the next one. `content` is derived instead — live props
  // while a course is open, the frozen copy while it animates out — so render itself stays pure.
  const lastContentRef = useRef<DrawerContent | null>(null);

  const content: DrawerContent | null = course
    ? { course, distanceMeters, nextCourse }
    : lastContentRef.current;

  useEffect(() => {
    if (course) {
      lastContentRef.current = { course, distanceMeters, nextCourse };
    }
  }, [course, distanceMeters, nextCourse]);

  if (!content) {
    return null;
  }

  const isOpen = course != null;
  const shown = content.course;
  const nextUp = content.nextCourse;

  const locationLine = [shown.stateName, shown.countryName]
    .filter(Boolean)
    .join(', ');

  return (
    <div
      // inset-y-0, the full height of the screen, same as MemberSetupDrawer. It used to start at
      // top-[5.5rem], under the top bar, which left it reading as a column parked below the search
      // row rather than as its own surface. z-50 puts it level with the top bar and it wins on DOM
      // order, so it covers the bar the way the sign-in drawer does.
      //
      // Full width on a phone, a 24rem column from sm up. It used to be `hidden sm:flex`, a
      // matched pair with the nearby strip: strip below the breakpoint, drawer above, never both.
      // That changed when the strip became a compact row with its own View button — the strip now
      // stays on screen at every width and this drawer is what View opens, so on a phone it has to
      // exist and it covers the map while it is out.
      //
      // pointer-events-none while closed: the panel is still in the tree during the slide-out, and
      // an invisible column must not eat clicks on the map.
      className={`fixed inset-y-0 right-0 z-50 flex w-full transform transition-transform duration-300 ease-in-out sm:w-96 ${
        isOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full'
      }`}
      aria-hidden={!isOpen}
      // pointer-events-none stops the mouse but not the keyboard: without `inert` the closed
      // drawer keeps Close, both checkboxes and the next-nearby row in the tab order, so tabbing
      // across the page lands on invisible controls that act on whichever course was last open.
      // Checkboxes make that worse than the buttons they replaced — a stray keystroke would flip
      // a stored state rather than fire a one-off action. It is
      // also what makes aria-hidden legal here — aria-hidden over focusable children is a
      // WCAG failure on its own.
      inert={!isOpen}
    >
      <div className="flex h-full w-full flex-col overflow-hidden border-l border-primary/30 bg-charcoal shadow-2xl">
        {/* Header bar. Titled "Club Details" rather than the course name — the name is the first
            thing in the body, and repeating it here spends the one heading line on a duplicate. */}
        <div className="relative flex shrink-0 items-center justify-center border-b border-white/10 bg-charcoal-light px-4 py-3">
          <h2 className="text-base font-semibold text-white">Club Details</h2>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 rounded-full p-1 text-white/50 transition-colors hover:bg-white/10 hover:text-primary"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Only this column scrolls, under the fixed header bar. There is no pinned footer any
            more: the two calls-to-action that used to live there are now checkboxes in the body
            (D1/D2), and Request to Play is hidden until after launch (D3). */}
        <div className="flex-1 overflow-y-auto">
          {/* Hero and logo badge, deliberately identical to CourseCard's: the same default header
              photo, the same id-derived crop so a club shows the same band of it here as in the
              list, the same scrim, and the same white plate overlapping the hero's bottom edge.
              Shared as markup rather than by rendering a whole CourseCard, because the drawer's
              body is a different thing — a played pill, an address block and four sections, not a
              name and two buttons. Both render CourseHero, so the one part that could silently
              drift, the crop, cannot. */}
          <CourseHero
            course={shown}
            distanceLabel={
              content.distanceMeters === undefined
                ? null
                : formatDistance(content.distanceMeters)
            }
            // Not lazy, unlike the list card. That card scrolls into view normally; this drawer is
            // parked off-viewport with translate-x-full, so a lazy image is not fetched until the
            // drawer opens and the first open slides in with an empty hero.
            eager
          />

          {/* pt-8 clears the badge, which hangs 1.25rem below the hero. Same figure as the card. */}
          <div className="px-5 pb-4 pt-8">
            <div className="flex flex-wrap items-center gap-1.5">
              {locationLine && (
                <span className="inline-block rounded border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                  {locationLine}
                </span>
              )}
              <ClosedBadge
                closed={shown.closed}
                closedYear={shown.closedYear}
                closedMonth={shown.closedMonth}
                className="py-1"
              />
            </div>

            <h3 className="mt-2 text-lg font-bold leading-snug text-white">
              {shown.name}
            </h3>

            {/* Both rows are links now, and both are gold. Address opens a map, phone dials.
                They used to be white/50 text with only the phone tappable, so the one live
                control on this panel looked exactly like the dead text above it. */}
            <CourseContactLinks course={shown} className="mt-2" />

            <ShareCourseButton course={shown} className="mt-3 w-full" />
          </div>

          <SectionBreak />

          <CourseStateToggles
            idPrefix="course"
            isPlayed={isPlayed}
            isBucketListed={isBucketListed}
            onTogglePlayed={() => onTogglePlayed(shown)}
            onBucketList={() => onBucketList(shown)}
            className="px-5 py-4"
          />

          {/* D3/D4: Request to Play, My GolfPlayed (friends played) and Game Reviews all used to
              render here as inert placeholders. All three are hidden until the features behind
              them exist — Request to Play is the first one due after launch. The markup is not
              kept commented out; git history has it, and a commented block rots. */}

          {/* Hop one step down the nearby ordering — the same sequence the strip scrolls through.
              Clicking it moves the map and re-opens the drawer on that course, which advances this
              row again, so repeated clicks walk the visitor outward club by club without going
              back to the map to hunt for a pin.
              D5 cut this back from a full CourseCard to four things: logo, name, country, address.
              No hero photo and no distance. That is why it is bespoke markup rather than the
              shared card — the shared card *is* a hero and a distance, and bending it into this
              shape with flags would have changed the list and the phone-only strip too.
              Last section on purpose — it is the way *out* of this club, so it sits after
              everything there is to read about the club itself. Hidden rather than disabled when
              there is nothing left, which means the end of the nearby set has been reached. */}
          {nextUp && (
            <>
              <SectionBreak />

              <div className="px-5 py-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/40">
                  Next Nearby Course
                </p>

                <CourseCompactCard
                  course={nextUp}
                  onSelect={() => onSelectCourse(nextUp)}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseDetailDrawer;
